import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api from '@/lib/api';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, House, ShieldStar, Users, UserPlus, EyeSlash, CheckCircle } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import ConfirmDialog from '@/components/ConfirmDialog';
import { toast } from 'sonner';

export default function AdminHouseholdOversight() {
    const { facilityId } = useParams();
    const nav = useNavigate();
    const qc = useQueryClient();
    const [joinOpen, setJoinOpen] = useState(false);

    const { data, isLoading } = useQuery({
        queryKey: ['admin-household', facilityId],
        queryFn: async () => (await api.get(`/admin/households/${facilityId}`)).data,
    });

    const doJoin = async () => {
        try {
            const { data: r } = await api.post(`/admin/households/${facilityId}/join`);
            if (r.already) {
                toast.info('Already a coordinator.');
            } else {
                toast.success('Joined as coordinator. You can now edit this household.');
            }
            qc.invalidateQueries({ queryKey: ['admin-household', facilityId] });
            qc.invalidateQueries({ queryKey: ['admin-households'] });
            // Navigate to the standard household detail page after joining
            nav(`/households/${facilityId}`);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Could not join');
        } finally {
            setJoinOpen(false);
        }
    };

    if (isLoading) return <div className="px-6 md:px-10 py-8 text-sm text-slate-500">Loading…</div>;
    if (!data) return <div className="px-6 md:px-10 py-8 text-sm text-slate-500">Household not found.</div>;

    const { household, members = [], read_only } = data;

    return (
        <div className="px-6 md:px-10 py-8 max-w-5xl" data-testid="admin-household-oversight-root">
            <Link to="/admin/households" className="text-sm text-slate-500 hover:text-slate-800 inline-flex items-center gap-1 mb-4">
                <ArrowLeft size={14} /> All households
            </Link>

            <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500 mb-2 flex items-center gap-1.5">
                <ShieldStar size={12} weight="duotone" /> Tanow staff · Oversight
            </div>

            <div className="flex items-start justify-between gap-4 mb-4">
                <div>
                    <h1 className="font-heading text-4xl md:text-5xl text-slate-900 flex items-center gap-3">
                        <House size={30} className="text-primary" /> {household.name}
                    </h1>
                    <p className="text-slate-600 mt-2">{household.kind || 'Household'}</p>
                </div>
                <div>
                    {read_only ? (
                        <Button
                            data-testid="admin-join-household"
                            onClick={() => setJoinOpen(true)}
                            className="bg-primary text-primary-foreground hover:bg-primary/90"
                        >
                            <UserPlus size={16} className="mr-2" /> Join as coordinator
                        </Button>
                    ) : (
                        <Link to={`/households/${facilityId}`} data-testid="admin-open-household">
                            <Button className="bg-primary text-primary-foreground hover:bg-primary/90">
                                <CheckCircle size={16} className="mr-2" /> Open (you&apos;re a coordinator)
                            </Button>
                        </Link>
                    )}
                </div>
            </div>

            {read_only && (
                <div
                    data-testid="admin-read-only-banner"
                    className="mb-6 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 flex items-start gap-2"
                >
                    <EyeSlash size={16} weight="duotone" className="mt-0.5 shrink-0" />
                    <div>
                        <div className="font-semibold">Viewing as admin — read only.</div>
                        <div className="text-amber-800/90 text-[13px] mt-0.5">
                            You can browse this household to help troubleshoot. To make changes, tap
                            <span className="mx-1 font-medium">Join as coordinator</span>
                            — the action is logged in the admin access trail.
                        </div>
                    </div>
                </div>
            )}

            <section className="rounded-xl border border-slate-200 bg-card shadow-sm overflow-hidden">
                <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-2 text-slate-800">
                    <Users size={16} /> <span className="font-semibold">{members.length} family member{members.length === 1 ? '' : 's'}</span>
                </div>
                {members.length === 0 ? (
                    <div className="px-5 py-8 text-sm text-slate-500 text-center">No family members in this household yet.</div>
                ) : (
                    <div className="divide-y divide-slate-100">
                        {members.map(m => (
                            <div key={m.patient_id} data-testid="admin-household-member-row" className="px-5 py-3 flex items-center justify-between">
                                <div>
                                    <div className="font-medium text-slate-900">{m.name}</div>
                                    <div className="text-[11px] text-slate-500">
                                        {m.date_of_birth ? `Born ${m.date_of_birth}` : ''}
                                        {m.primary_diagnosis ? ` · ${m.primary_diagnosis}` : ''}
                                    </div>
                                </div>
                                {read_only ? (
                                    <span className="text-[10px] uppercase tracking-widest text-slate-500">Read only</span>
                                ) : (
                                    <Link to={`/members/${m.patient_id}`} className="text-sm text-primary hover:underline">Open</Link>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </section>

            <ConfirmDialog
                open={joinOpen}
                onOpenChange={setJoinOpen}
                title={`Join ${household.name} as coordinator?`}
                description="You'll be added to the coordinator list for this household and will be able to make changes. This action is recorded in the admin access log."
                confirmLabel="Yes, join as coordinator"
                onConfirm={doJoin}
            />
        </div>
    );
}
